import React, { useState, useEffect } from 'react';
import MobileScrollInput from '../MobileScrollInput';
import '../../styles.css';

function TireTemperatureInput({ value, onChange, help, setHelpParam, setShowHelpModal, param }) {
  const [showDialog, setShowDialog] = useState(false);
  const [temperatures, setTemperatures] = useState(value || Array(12).fill(''));

  useEffect(() => {
    setTemperatures(value || Array(12).fill(''));
  }, [value]);

  const getTemperatureColor = (temp) => {
    if (!temp || isNaN(temp)) return '#333';
    const thresholds = param?.colorThresholds;
    if (!thresholds) return '#333';

    const cold = parseFloat(thresholds.cold);
    const warm = parseFloat(thresholds.warm);
    const hot = parseFloat(thresholds.hot);
    const tooHot = parseFloat(thresholds.tooHot);
    const ridiculouslyHot = parseFloat(thresholds.ridiculouslyHot);

    if (temp < cold) return '#0000FF'; // blue
    if (temp < warm) return '#74d2bfff'; // sky blue
    if (temp < hot) return '#26a426ff'; // green
    if (temp < tooHot) return '#9db914ff'; // goldenrod
    if (temp < ridiculouslyHot) return '#d69b13ff'; // orange red
    return '#d80d0dff'; // red
  };

  const handleSave = () => {
    onChange(temperatures);
    setShowDialog(false);
  };

  const handleClose = () => {
    setTemperatures(value || Array(12).fill(''));
    setShowDialog(false);
  };

  const renderDisplay = () => {
    // FL: 0-2 (O M I), FR: 3-5, RL: 6-8, RR: 9-11
    // The display is O M I | I M O for front, then rear
    // For front: FL O M I | FR I M O ? Wait, the user said O M I | I M O
    // Probably FL O M I | FR O M I, but the user said I M O for right side?
    // "O M I | I M O" but I think it's a typo, probably O M I | O M I
    // But user said "O M I | I M O" – perhaps for left it's O M I, right is I M O meaning inside middle outside for right side.
    // For left side, outside is left, inside is right, but for tires, inside/outside is relative to car.
    // For FL: outside is left of car, inside is right of car.
    // But for display, perhaps it's FL O M I | FR O M I, but user wrote I M O for second.
    // Looking: "O M I | I M O" – probably FL O M I | FR I M O, meaning for right side, inside is first.
    // To match, let's assume corners[0] is FL O M I, corners[1] is FR I M O
    // So for display: corners[0] | corners[1].split(' ').reverse().join(' ') or something.
    // User: "O M I | I M O" – so first O M I (FL), second I M O (FR)
    // So FR is I M O, meaning inside, middle, outside for FR.
    // So in array, for FR: index 3: I, 4: M, 5: O
    // For display, front line: temperatures[0] temperatures[1] temperatures[2] | temperatures[3] temperatures[4] temperatures[5]
    // Rear: temperatures[6] temperatures[7] temperatures[8] | temperatures[9] temperatures[10] temperatures[11]
    return (
      <div className="tire-corners">
        <div className="tire-corner">
          <div className="corner-label">FL</div>
          <div className="corner-temps">
            <span style={{ color: getTemperatureColor(temperatures[0]) }}>{temperatures[0] || '-'}</span>
            <span style={{ color: getTemperatureColor(temperatures[1]) }}>{temperatures[1] || '-'}</span>
            <span style={{ color: getTemperatureColor(temperatures[2]) }}>{temperatures[2] || '-'}</span>
          </div>
        </div>
        <div className="tire-corner">
          <div className="corner-label">FR</div>
          <div className="corner-temps">
            <span style={{ color: getTemperatureColor(temperatures[3]) }}>{temperatures[3] || '-'}</span>
            <span style={{ color: getTemperatureColor(temperatures[4]) }}>{temperatures[4] || '-'}</span>
            <span style={{ color: getTemperatureColor(temperatures[5]) }}>{temperatures[5] || '-'}</span>
          </div>
        </div>
        <div className="tire-corner">
          <div className="corner-label">RL</div>
          <div className="corner-temps">
            <span style={{ color: getTemperatureColor(temperatures[6]) }}>{temperatures[6] || '-'}</span>
            <span style={{ color: getTemperatureColor(temperatures[7]) }}>{temperatures[7] || '-'}</span>
            <span style={{ color: getTemperatureColor(temperatures[8]) }}>{temperatures[8] || '-'}</span>
          </div>
        </div>
        <div className="tire-corner">
          <div className="corner-label">RR</div>
          <div className="corner-temps">
            <span style={{ color: getTemperatureColor(temperatures[9]) }}>{temperatures[9] || '-'}</span>
            <span style={{ color: getTemperatureColor(temperatures[10]) }}>{temperatures[10] || '-'}</span>
            <span style={{ color: getTemperatureColor(temperatures[11]) }}>{temperatures[11] || '-'}</span>
          </div>
        </div>
      </div>
    );
  };



  return (
    <div className="param-card" onClick={() => setShowDialog(true)} style={{ cursor: 'pointer' }}>
      <h3>
        Tire Temperature{' '}
        <i onClick={(e) => { e.stopPropagation(); setHelpParam(param); setShowHelpModal(true); }} className="material-icons">info</i>
      </h3>
      {renderDisplay()}
      <div className="param-unit">{param?.unit || '°C'}</div>
      {showDialog && (
        <div className="overlay-card" onClick={handleClose}>
          <div className="card-container" style={{ maxWidth: '900px' }} onClick={(e) => e.stopPropagation()}>
            <div className="page-title" style={{ paddingBottom: '20px' }}>
              <h1>Record Tire Temperatures</h1>
              <button className="close-button" onClick={handleClose}>×</button>
            </div>
            <div className="tire-modal-grid">
              <div className="tire-section">
                <h4>Front Left</h4>
                <div className="tire-temps">
                  {temperatures.slice(0, 3).map((temp, idx) => (
                    <div key={idx} className="temp-input-wrapper" style={{ borderBottom: `2px solid ${getTemperatureColor(temp)}` }}>
                      <MobileScrollInput
                        value={temp}
                        onChange={(value) => {
                          const newTemps = [...temperatures];
                          newTemps[idx] = value;
                          setTemperatures(newTemps);
                        }}
                        step={0.1}
                        min={0}
                        max={200}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="tire-section">
                <h4>Front Right</h4>
                <div className="tire-temps">
                  {temperatures.slice(3, 6).map((temp, idx) => (
                    <div key={idx + 3} className="temp-input-wrapper" style={{ borderBottom: `2px solid ${getTemperatureColor(temp)}` }}>
                      <MobileScrollInput
                        value={temp}
                        onChange={(value) => {
                          const newTemps = [...temperatures];
                          newTemps[idx + 3] = value;
                          setTemperatures(newTemps);
                        }}
                        step={0.1}
                        min={0}
                        max={200}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="tire-section">
                <h4>Rear Left</h4>
                <div className="tire-temps">
                  {temperatures.slice(6, 9).map((temp, idx) => (
                    <div key={idx + 6} className="temp-input-wrapper" style={{ borderBottom: `2px solid ${getTemperatureColor(temp)}` }}>
                      <MobileScrollInput
                        value={temp}
                        onChange={(value) => {
                          const newTemps = [...temperatures];
                          newTemps[idx + 6] = value;
                          setTemperatures(newTemps);
                        }}
                        step={0.1}
                        min={0}
                        max={200}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="tire-section">
                <h4>Rear Right</h4>
                <div className="tire-temps">
                  {temperatures.slice(9, 12).map((temp, idx) => (
                    <div key={idx + 9} className="temp-input-wrapper" style={{ borderBottom: `2px solid ${getTemperatureColor(temp)}` }}>
                      <MobileScrollInput
                        value={temp}
                        onChange={(value) => {
                          const newTemps = [...temperatures];
                          newTemps[idx + 9] = value;
                          setTemperatures(newTemps);
                        }}
                        step={0.1}
                        min={0}
                        max={200}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ textAlign: 'center', position: 'relative', marginBottom: '22px' }}>
              <div className="param-unit">{param?.unit || '°C'}</div>
              <button onClick={handleSave} className="btn btn-save" style={{ position: 'absolute', right: 0, top: 0 }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TireTemperatureInput;